import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MessageCircle, Heart, ArrowRight, BarChart3, CalendarDays, Share2, FileText, Link as LinkIcon, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SharePanel from '../components/SharePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCENT = '#7F77DD';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const getYouTubeId = (url) => {
  const m = String(url || '').match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
};

const Avatar = ({ src, name, size = 'h-12 w-12', text = '' }) => src ? (
  <img src={resolveUrl(src)} alt="" className={`${size} rounded-full object-cover flex-shrink-0`} />
) : (
  <div className={`${size} rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center font-bold flex-shrink-0 ${text}`}>
    {(name || '?').substring(0, 2).toUpperCase()}
  </div>
);

/* Brief LOT3 — page publique dédiée d'une publication partagée (SANS compte).
   Brief 27 oct B.3 — refonte : partage ouvert, aperçu des commentaires,
   CTA de conversion clairs (réagir / suivre l'auteur / créer un compte). */
export default function PublicPostPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public/post/${postId}`)
      .then(({ data }) => { if (alive) { setData(data); document.title = `${data.author?.display_name || 'Publication'} sur OpenSapiens`; } })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [postId]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAF8] px-4 text-center" data-testid="public-post-notfound">
        <p className="text-2xl font-bold text-gray-900">Publication indisponible</p>
        <p className="text-gray-500 mt-2">Elle a été retirée ou n&apos;est pas publique.</p>
        <Link to="/" className="mt-6 text-[#7F77DD] font-semibold hover:underline">Découvrir OpenSapiens →</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  const { post, author, comments = [] } = data;
  const dateStr = post.created_at
    ? new Date(post.created_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  const isImage = post.media_url && /\.(jpg|jpeg|png|gif|webp)($|\?)/i.test(post.media_url);
  const ytId = getYouTubeId(post.media_url);
  const isDoc = post.og_data?.type === 'document' || /\.pdf($|\?)/i.test(post.media_url || '');
  const cleanContent = (post.content || '')
    .replace(/\[product:[^\]]+\]/g, '')
    .replace(/\[community:[^\]]+\]/g, '')
    .trim();
  const appUrl = `/app/feed?post=${post.post_id}&comments=1`;
  const authUrl = (kind) => `/${kind}?redirect=${encodeURIComponent(appUrl)}`;
  const shareUrl = `${window.location.origin}/post/${post.post_id}`;
  const authorPublic = author.handle ? `/@${author.handle}` : null;
  const firstName = (author.display_name || '').split(' ')[0];

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="public-post-page">
      <nav className="bg-white/80 backdrop-blur border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 h-12 flex items-center justify-between">
          <Link to="/" className="text-base font-bold bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            OpenSapiens
          </Link>
          {user ? (
            <button onClick={() => navigate(appUrl)} className="text-sm font-medium text-[#7F77DD] hover:underline" data-testid="public-post-open-app-top">Ouvrir dans l&apos;app →</button>
          ) : (
            <div className="flex items-center gap-3">
              <Link to={authUrl('login')} className="text-sm text-gray-600 hover:text-gray-900" data-testid="public-post-login">Se connecter</Link>
              <Link to={authUrl('signup')} className="text-sm font-semibold text-white px-3.5 py-1.5 rounded-lg hover:opacity-90" style={{ backgroundColor: ACCENT }} data-testid="public-post-signup-btn">
                Rejoindre
              </Link>
            </div>
          )}
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <article className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Auteur */}
          <div className="p-5 flex items-center gap-3 border-b border-gray-50">
            {authorPublic ? (
              <Link to={authorPublic} className="flex-shrink-0"><Avatar src={author.avatar} name={author.display_name} /></Link>
            ) : <Avatar src={author.avatar} name={author.display_name} />}
            <div className="flex-1 min-w-0">
              {authorPublic ? (
                <Link to={authorPublic} className="font-semibold text-gray-900 hover:text-[#7F77DD] transition-colors" data-testid="public-post-author">{author.display_name}</Link>
              ) : (
                <p className="font-semibold text-gray-900" data-testid="public-post-author">{author.display_name}</p>
              )}
              <p className="text-xs text-gray-500">{author.expert_pitch ? <span className="line-clamp-1">{author.expert_pitch}</span> : dateStr}</p>
            </div>
            <button
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-[#7F77DD]/60 hover:text-[#7F77DD] transition-colors flex-shrink-0"
              data-testid="public-post-share-btn"
            >
              <Share2 className="h-4 w-4" /> <span className="hidden sm:inline">Partager</span>
            </button>
          </div>

          <div className="p-5 space-y-4">
            {cleanContent && (
              <p className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-line" data-testid="public-post-content">{cleanContent}</p>
            )}

            {post.post_type === 'poll' && post.poll && (
              <div className="p-4 bg-gray-50 rounded-xl" data-testid="public-post-poll">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="h-5 w-5 text-purple-500" />
                  <h4 className="font-semibold text-gray-900">{post.poll.question}</h4>
                </div>
                <div className="space-y-2">
                  {(post.poll.options || []).map((opt) => {
                    const total = post.poll.total_votes || 0;
                    const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
                    return (
                      <div key={opt.option_id} className="relative bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-purple-100" style={{ width: `${pct}%` }} />
                        <div className="relative flex items-center justify-between px-4 py-2.5 text-sm">
                          <span className="text-gray-700">{opt.text}</span>
                          <span className="font-bold text-gray-500">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  {post.poll.total_votes || 0} vote{(post.poll.total_votes || 0) !== 1 ? 's' : ''} · <button onClick={() => navigate(user ? appUrl : authUrl('signup'))} className="text-[#7F77DD] font-medium hover:underline">voter dans l&apos;app</button>
                </p>
              </div>
            )}

            {post.event_data && (
              <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl" data-testid="public-post-event">
                <div className="flex items-center gap-2 text-purple-700 text-xs font-bold uppercase tracking-wider mb-1">
                  <CalendarDays className="h-4 w-4" /> Événement
                </div>
                <h4 className="font-semibold text-gray-900">{post.event_data.title}</h4>
                {post.event_data.start_time && (
                  <p className="text-sm text-gray-600 mt-0.5">
                    {new Date(post.event_data.start_time).toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                )}
                <button
                  onClick={() => navigate(`/event/${post.event_data.event_id}`)}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-white px-4 py-2 rounded-lg hover:opacity-90"
                  style={{ backgroundColor: ACCENT }}
                  data-testid="public-post-event-cta"
                >
                  Voir l&apos;événement <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {ytId ? (
              <div className="rounded-xl overflow-hidden bg-black">
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <iframe src={`https://www.youtube.com/embed/${ytId}`} title="YouTube" className="absolute inset-0 w-full h-full" frameBorder="0" allowFullScreen />
                </div>
              </div>
            ) : isImage ? (
              <img src={resolveUrl(post.media_url)} alt="" className="w-full max-h-[480px] object-contain bg-gray-50 rounded-xl" data-testid="public-post-image" />
            ) : post.og_data?.image ? (
              <a href={post.media_url} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden border border-gray-200 hover:shadow-md transition-shadow" data-testid="public-post-link-card">
                <img src={post.og_data.image} alt="" className="w-full h-48 object-cover" />
                <div className="p-3 bg-white">
                  <p className="font-semibold text-gray-900 line-clamp-1">{post.og_data.title || post.media_url}</p>
                  {post.og_data.description && <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{post.og_data.description}</p>}
                  <p className="text-xs text-[#7F77DD] mt-1.5 inline-flex items-center gap-1"><LinkIcon className="h-3 w-3" /> {post.og_data.domain || post.media_url}</p>
                </div>
              </a>
            ) : post.media_url ? (
              <a href={resolveUrl(post.media_url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-testid="public-post-link">
                <div className="h-10 w-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center">{isDoc ? <FileText className="h-5 w-5 text-blue-600" /> : <LinkIcon className="h-5 w-5 text-[#7F77DD]" />}</div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{post.og_data?.title || post.media_url}</p>
                  <p className="text-xs text-gray-500">{isDoc ? 'Document' : (post.og_data?.domain || 'Lien externe')}</p>
                </div>
              </a>
            ) : null}

            <div className="flex items-center justify-between pt-3 border-t border-gray-50">
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="inline-flex items-center gap-1"><Heart className="h-4 w-4" /> {post.like_count || 0}</span>
                <span className="inline-flex items-center gap-1"><MessageCircle className="h-4 w-4" /> {post.comment_count || 0}</span>
                <span className="text-xs text-gray-400 hidden sm:inline">{dateStr}</span>
              </div>
              <button
                onClick={() => navigate(user ? appUrl : authUrl('signup'))}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-4 py-2 rounded-lg hover:opacity-90"
                style={{ backgroundColor: ACCENT }}
                data-testid="public-post-react-btn"
              >
                <Heart className="h-4 w-4" /> Réagir
              </button>
            </div>
          </div>

          {/* Aperçu des commentaires */}
          {comments.length > 0 && (
            <div className="border-t border-gray-100 bg-gray-50/60 p-5 space-y-3" data-testid="public-post-comments">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                {post.comment_count > comments.length ? `Derniers commentaires (${comments.length} sur ${post.comment_count})` : 'Commentaires'}
              </p>
              {comments.map((c) => (
                <div key={c.comment_id} className="flex items-start gap-2.5" data-testid={`public-post-comment-${c.comment_id}`}>
                  <Avatar src={c.author_avatar} name={c.author_name} size="h-8 w-8" text="text-xs" />
                  <div className="flex-1 min-w-0 bg-white rounded-2xl rounded-tl-sm px-3.5 py-2.5 border border-gray-100">
                    <p className="text-xs font-semibold text-gray-800">{c.author_name}</p>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{c.content}</p>
                  </div>
                </div>
              ))}
              <button onClick={() => navigate(user ? appUrl : authUrl('signup'))} className="w-full text-left text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-full px-4 py-2.5 hover:border-[#7F77DD]/60 hover:text-[#7F77DD] transition-colors" data-testid="public-post-comment-cta">
                Écrire un commentaire…
              </button>
            </div>
          )}
        </article>

        {/* Bloc conversion */}
        <section className="mt-6 rounded-3xl p-7 text-center text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #7F77DD 0%, #A855F7 60%, #EC4899 100%)' }} data-testid="public-post-conversion">
          <h2 className="text-xl font-extrabold tracking-tight">Rejoignez la conversation</h2>
          <p className="mt-1.5 text-white/85 text-sm max-w-md mx-auto">
            Réagissez, commentez et suivez {firstName || author.display_name} — gratuit pendant la beta.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            {user ? (
              <button onClick={() => navigate(appUrl)} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-white text-[#7F77DD] hover:bg-gray-50 shadow" data-testid="public-post-open-app">
                Ouvrir dans OpenSapiens <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <>
                <Link to={authUrl('signup')} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-white text-[#7F77DD] hover:bg-gray-50 shadow" data-testid="public-post-open-app">
                  Créer un compte gratuit <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to={authUrl('login')} className="inline-flex items-center px-5 py-3 rounded-xl text-sm font-semibold text-white border border-white/50 hover:bg-white/10" data-testid="public-post-login-cta">
                  J&apos;ai déjà un compte
                </Link>
              </>
            )}
            {authorPublic && (
              <Link to={authorPublic} className="inline-flex items-center gap-1.5 px-5 py-3 rounded-xl text-sm font-semibold text-white border border-white/50 hover:bg-white/10" data-testid="public-post-author-link">
                <UserPlus className="h-4 w-4" /> Voir le profil de {firstName}
              </Link>
            )}
          </div>
        </section>

        <p className="text-center text-xs text-gray-400 mt-6">
          <Link to="/" className="hover:text-gray-600">Propulsé par <span className="font-semibold">OpenSapiens</span> — la plateforme suisse d&apos;échange de savoirs</Link>
        </p>
      </main>

      <SharePanel
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Partager cette publication"
        shareUrl={shareUrl}
        prettyUrl={shareUrl}
        defaultMessage={`À lire : ${cleanContent.substring(0, 90)}${cleanContent.length > 90 ? '…' : ''}`}
      />
    </div>
  );
}
